'use client';

import ProgressRing from 'src/components/Progress/Ring';
import Heading from 'src/components/Heading/Heading';
import { Accordion, Button } from '@get-mosh/msh-shared';
import Image from 'next/image';
import HairDoctorCard, { HairDoctorCardProps } from './HairDoctorCard';
import ProgressBar from 'src/components/Progress/Bar';
import { trackStartMyFreeConsultClick } from 'src/analytics/Events';
import Routes from 'src/constants/Routes';
import commonData from '../../data/common.json';
import styles from './hair.module.scss';
import { Vertical } from 'src/constants/Verticals';
import Link from 'next/link';

export type HairFactsSectionProps = {
    data: {
        title: string;
        treatments: {
            accordion: {
                title: string;
                content: string;
            }[];
        };
        causes: {
            accordion: {
                title: string;
                content: string;
            }[];
        };
        doctorCard: HairDoctorCardProps['data'];
    };
};

const HairFactsSection = ({ data }: HairFactsSectionProps) => {
    const handleClick = () => {
        trackStartMyFreeConsultClick({
            vertical: Vertical.HAIR,
            location: 'doctor facts',
        });
    };

    return (
        <>
            <Heading tag="p" level="h2" centered={true} className="mb-8">
                {data.title}
            </Heading>
            <div className="flex flex-row flex-wrap lg:container lg:mt-12">
                <div className="w-full md:w-1/2 md:pr-12 xl:w-1/3">
                    <HairDoctorCard data={data.doctorCard} />
                </div>
                <div
                    className={`${styles['fact-info']} w-full md:ml-auto md:w-1/2 md:grow-0 md:pr-2 xl:w-2/3 xl:pl-32`}
                >
                    <div className={`${styles['fact']}`}>
                        <Heading tag="p" level="h5">
                            Hair loss is very common
                        </Heading>
                        <div className="flex flex-nowrap items-center">
                            <ProgressRing progress={85} />
                            <p>
                                of men will have significantly thinner hair by
                                the age of 50
                                <small className="block underline">
                                    <a
                                        href="https://www.americanhairloss.org/men_hair_loss/introduction.html"
                                        target="blank"
                                        title="85% of men will have significantly thinner hair by the age of 50"
                                    >
                                        American Hair Loss Association
                                    </a>
                                </small>
                            </p>
                        </div>
                    </div>
                    <div className={`${styles['causes']}`}>
                        <Heading tag="p" level="h5">
                            There are known causes of hair loss
                        </Heading>
                        <ul
                            className={`${styles['causes-list']} flex flex-nowrap items-center py-4`}
                        >
                            <li className="md:1/4 flex w-1/3 flex-col flex-wrap self-end px-2 xl:w-1/5">
                                <Image
                                    src={
                                        '/images/icons/hair-icon-hormonal-effects.svg'
                                    }
                                    className={'mx-auto mb-2'}
                                    width={40}
                                    height={32}
                                    alt=""
                                />
                                Hormonal <br />
                                effects
                            </li>
                            <li className="md:1/4 flex w-1/3 flex-col flex-wrap self-end px-2 xl:w-1/5">
                                <Image
                                    src={
                                        '/images/icons/hair-icon-genetic-factors.svg'
                                    }
                                    className={'mx-auto mb-2'}
                                    width={25}
                                    height={32}
                                    alt=""
                                />
                                Genetic <br />
                                factors
                            </li>
                            <li className="md:1/4 flex w-1/3 flex-col flex-wrap self-end px-2 xl:w-1/5">
                                <Image
                                    src={
                                        '/images/icons/hair-icon-lack-of-nutrients.svg'
                                    }
                                    className={'mx-auto mb-2'}
                                    width={43}
                                    height={42}
                                    alt=""
                                />
                                Lack of <br />
                                nutrients
                            </li>
                        </ul>
                        <Accordion
                            className="!border-0"
                            itemClassName="bg-sand-light"
                            items={data.causes.accordion}
                        />
                    </div>
                    <div className={`${styles['questions']}`}>
                        <Heading tag="p" level="h5">
                            Fortunately there are effective treatments
                        </Heading>
                        <div className={`${styles['info-percentage-wrapper']}`}>
                            <div
                                className={`${styles['info-percentage']} mb-4 flex items-center gap-4 space-x-4`}
                            >
                                <div className="w-1/3">
                                    <ProgressBar
                                        progress={90}
                                        max={100}
                                        showText={true}
                                    />
                                </div>
                                <div className="w-2/3">
                                    <p>
                                        will keep their hair
                                        <a
                                            href="https://doi.org/10.1046/j.1523-1747.2003.12167.x"
                                            target="blank"
                                            title="90% of men will keep their hair"
                                        >
                                            https://doi.org
                                        </a>
                                    </p>
                                </div>
                            </div>
                            <div
                                className={`${styles['info-percentage']} flex items-center gap-4 space-x-4`}
                            >
                                <div className="w-1/3">
                                    <ProgressBar
                                        progress={83}
                                        max={100}
                                        showText={true}
                                    />
                                </div>
                                <div className="w-2/3">
                                    <p>
                                        will regrow their hair
                                        <a
                                            href="https://www.ijord.com/index.php/ijord/article/view/394"
                                            target="blank"
                                            title="90% of men will regrow their hair"
                                        >
                                            https://www.ijord.com
                                        </a>
                                    </p>
                                </div>
                            </div>
                        </div>
                        <Accordion
                            className="!border-0"
                            itemClassName="bg-sand-light"
                            items={data.treatments.accordion}
                        />
                    </div>
                </div>
            </div>
            <div className="mx-auto block text-center md:p-4">
                <Button
                    as={Link}
                    text={commonData.takeTheQuiz}
                    onClick={handleClick}
                    href={Routes.HAIR_LOSS_INITIAL_QUIZ}
                    stretch
                />
            </div>
        </>
    );
};

export default HairFactsSection;
